import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { User, Phone, Lock, Loader2, ArrowLeft, Sparkles, ShieldCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";

const premiumCardMotionStyles = `
  @keyframes portal-card-float {
    0%, 100% { transform: perspective(1200px) rotateX(1deg) rotateY(-2deg) translateY(0); }
    50% { transform: perspective(1200px) rotateX(-2deg) rotateY(3deg) translateY(-5px); }
  }
  @keyframes portal-card-sheen {
    0%, 55% { transform: translateX(-150%) rotate(18deg); opacity: 0; }
    65% { opacity: .7; }
    82%, 100% { transform: translateX(160%) rotate(18deg); opacity: 0; }
  }
  @media (prefers-reduced-motion: reduce) {
    .portal-card-motion, .portal-card-sheen { animation: none !important; }
  }
`;

export default function CustomerPortalLogin({
  restaurantId, restaurantLogo, restaurantName, step, phone, setPhone, code, setCode, name, setName,
  loading, countdown, waAutoSent, restaurantPhone,
  onRequestCode, onVerifyCode, onBack,
}) {
  const navigate = useNavigate();
  const hasLogo = Boolean(restaurantLogo);
  const tenantName = restaurantName || "Restaurante";

  return (
    <main className="min-h-screen bg-[#090909] text-white overflow-hidden relative">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(191,35,39,0.28),transparent_42%),radial-gradient(circle_at_bottom_left,rgba(197,156,62,0.14),transparent_40%)]" />
      <div className="relative max-w-md mx-auto px-5 py-8 sm:py-12">
        <header className="flex items-center justify-between mb-10">
          <div className="flex items-center gap-3">
            {hasLogo ? (
              <img src={restaurantLogo} alt="KAISŌ Sushi" className="h-14 w-14 rounded-2xl object-contain bg-white p-1 shadow-[0_0_30px_rgba(197,156,62,0.25)]" />
            ) : (
              <div className="h-14 w-14 rounded-2xl border border-[#c59c3e]/60 bg-black flex items-center justify-center">
                <Sparkles className="h-6 w-6 text-[#e5c15b]" />
              </div>
            )}
            <div>
              <p className="text-[11px] font-semibold tracking-[0.28em] text-[#e5c15b]">{tenantName.toUpperCase()}</p>
              <p className="text-sm text-white/55">Portal del cliente</p>
            </div>
          </div>
          <div className="rounded-full border border-[#c9a227]/70 bg-[#c9a227]/10 px-3 py-1 text-[11px] font-bold tracking-[0.16em] text-[#f4d66d] shadow-[0_0_18px_rgba(201,162,39,0.18)]">
            CLUB KAISŌ
          </div>
        </header>

        <section className="relative mb-7 flex flex-col items-center text-center">
          <div className="relative mb-5 h-44 w-44 sm:h-52 sm:w-52" aria-hidden="true">
            <div className="absolute inset-0 rounded-full bg-[radial-gradient(circle,rgba(197,156,62,0.30),transparent_65%)] blur-2xl portal-card-sheen" />
            <div className="absolute inset-3 rounded-full border border-[#c59c3e]/25 animate-[spin_24s_linear_infinite]">
              <span className="absolute -top-1 left-1/2 h-2 w-2 -translate-x-1/2 rounded-full bg-[#f4d66d] shadow-[0_0_16px_4px_rgba(244,214,109,0.7)]" />
            </div>
            <div className="absolute inset-8 rounded-full border border-dashed border-white/10 animate-[spin_36s_linear_infinite_reverse]" />
            <div className="absolute inset-0 rounded-full border border-[#bf2327]/25" />
            <div className="absolute left-1/2 top-1/2 flex h-28 w-28 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-[28px] border border-[#e5c15b]/65 bg-black shadow-[0_0_48px_-8px_rgba(197,156,62,0.65)] portal-card-motion">
              {hasLogo ? <img src={restaurantLogo} alt={tenantName} className="h-24 w-24 rounded-[20px] bg-white object-contain p-2" /> : <Sparkles className="h-9 w-9 text-[#e5c15b]" />}
            </div>
          </div>
          <p className="text-xs font-semibold uppercase tracking-[0.28em] text-[#e5c15b]">Tu experiencia empieza aquí</p>
          <h1 className="mt-3 text-4xl font-bold leading-[1.08] tracking-tight">Todo tu {tenantName},<br />en un solo lugar.</h1>
          <p className="mt-4 max-w-sm text-base leading-6 text-white/60">Consulta tus pedidos, tu crédito y tus ventajas de fidelidad. Sin descargar nada.</p>
        </section>

        <>
          <style>{premiumCardMotionStyles}</style>
          <div className="portal-card-motion relative mb-7" style={{ transformStyle: "preserve-3d", animation: "portal-card-float 7s ease-in-out infinite" }}>
            <section className="relative overflow-hidden rounded-[26px] border border-[#c9a227]/70 bg-[#08090b] p-5 text-white shadow-[0_22px_60px_rgba(0,0,0,0.42),0_0_34px_rgba(197,156,62,0.12)]" data-testid="login-loyalty-card">
          <div className="pointer-events-none absolute -inset-16 opacity-70">
            <div className="absolute left-1/2 top-1/2 h-[150%] w-[72%] -translate-x-1/2 -translate-y-1/2 rotate-[-18deg] rounded-[50%] border border-[#e7c75f]/35 animate-[spin_18s_linear_infinite]" />
            <div className="absolute left-1/2 top-1/2 h-[130%] w-[60%] -translate-x-1/2 -translate-y-1/2 rotate-[28deg] rounded-[50%] border border-[#c62828]/30 animate-[spin_24s_linear_infinite_reverse]" />
            <span className="absolute left-[12%] top-[28%] h-2 w-2 rounded-full bg-[#f4d66d] shadow-[0_0_16px_4px_rgba(244,214,109,0.8)] animate-pulse" />
            <span className="absolute right-[14%] bottom-[24%] h-1.5 w-1.5 rounded-full bg-[#e34a4d] shadow-[0_0_14px_4px_rgba(227,74,77,0.75)] animate-pulse" />
          </div>
          <div className="relative flex items-start justify-between gap-4">
            <div>
              <p className="text-[10px] font-black uppercase tracking-[0.24em] text-[#e7c75f]">Club {restaurantName || "del restaurante"}</p>
              <h2 className="mt-2 text-2xl font-black tracking-tight">Tu dinero vuelve contigo</h2>
              <p className="mt-1 text-sm leading-5 text-white/60">Gana puntos en cada compra y conviértelos en crédito.</p>
            </div>
            {hasLogo ? <img src={restaurantLogo} alt={restaurantName || "Restaurante"} className="h-14 w-14 rounded-2xl object-contain bg-white p-1.5 shadow-[0_0_24px_rgba(255,255,255,0.16)]" /> : <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-[#e7c75f]/50 bg-black/50 text-[10px] font-black tracking-widest text-[#e7c75f]">CLUB</div>}
          </div>
          <div className="relative mt-5 flex items-center justify-between border-t border-white/10 pt-3 text-xs">
            <span className="text-white/45">Fidelidad exclusiva</span>
            <span className="font-semibold text-[#f4d66d]">100 puntos = 10 €</span>
          </div>
            <div className="portal-card-sheen pointer-events-none absolute inset-y-0 left-0 z-20 w-1/3 bg-gradient-to-r from-transparent via-white/20 to-transparent" style={{ animation: "portal-card-sheen 8s ease-in-out infinite" }} />
            </section>
          </div>
        </>

        {step === "phone" && (
          <Card className="border border-white/10 bg-white/[0.07] text-white shadow-2xl backdrop-blur-xl">
            <CardHeader className="pb-3">
              <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-xl bg-[#bf2327]/20 text-[#f05a5d]">
                <Phone className="h-5 w-5" />
              </div>
              <CardTitle className="text-xl">Entrar con tu teléfono</CardTitle>
              <CardDescription className="pt-1 text-white/55">
                Te enviaremos un código de acceso por WhatsApp.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <label className="text-sm font-medium text-white/75" htmlFor="portal-phone">Número de teléfono</label>
              <div className="relative">
                <Phone className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-white/35" />
                <Input
                  id="portal-phone"
                  type="tel"
                  placeholder="+34 600 000 000"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="h-14 border-white/15 bg-black/25 pl-12 text-base text-white placeholder:text-white/30 focus-visible:ring-[#e5c15b]"
                  data-testid="phone-input"
                />
              </div>
              <Button
                onClick={onRequestCode}
                disabled={loading || !phone}
                className="h-14 w-full rounded-xl bg-[#bf2327] text-base font-semibold text-white shadow-lg shadow-red-950/30 hover:bg-[#d12c31]"
                data-testid="request-code-btn"
              >
                {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Enviando código...</> : "Continuar"}
              </Button>
              <div className="flex items-center justify-center gap-2 pt-1 text-xs text-white/45">
                <ShieldCheck className="h-4 w-4 text-[#e5c15b]" />
                Acceso seguro y privado
              </div>
            </CardContent>
          </Card>
        )}

        {step === "code" && (
          <Card className="border border-white/10 bg-white/[0.07] text-white shadow-2xl backdrop-blur-xl">
            <CardHeader className="pb-3">
              <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-xl bg-[#bf2327]/20 text-[#f05a5d]">
                <Lock className="h-5 w-5" />
              </div>
              <CardTitle className="text-xl">Verifica tu acceso</CardTitle>
              <CardDescription className="pt-1 text-white/55">
                {waAutoSent
                  ? "El código se ha enviado a tu WhatsApp. Introdúcelo abajo."
                  : "Envía la palabra CODIGO al WhatsApp del restaurante para recibirlo."}
              </CardDescription>
            </CardHeader>
            {restaurantPhone && (
              <div className="px-6 pb-2">
                <a
                  href={"https://wa.me/" + restaurantPhone + "?text=CODIGO"}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex w-full items-center justify-center gap-2 rounded-xl border border-[#35d06f]/30 bg-[#25d366] py-3.5 text-sm font-semibold text-white transition hover:bg-[#1fbd5b]"
                >
                  Enviar CODIGO por WhatsApp
                </a>
                <p className="mt-2 text-center text-xs text-white/40">Se abrirá WhatsApp con el mensaje preparado.</p>
              </div>
            )}
            <CardContent className="space-y-4">
              <label className="text-sm font-medium text-white/75" htmlFor="portal-code">Código de 6 dígitos</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-white/35" />
                <Input
                  id="portal-code"
                  type="text"
                  inputMode="numeric"
                  placeholder="000000"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\\D/g, ""))}
                  className="h-14 border-white/15 bg-black/25 pl-12 text-center text-2xl tracking-[0.35em] text-white placeholder:text-white/25 focus-visible:ring-[#e5c15b]"
                  data-testid="code-input"
                />
              </div>
              <Input
                type="text"
                placeholder="Tu nombre (opcional)"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="h-14 border-white/15 bg-black/25 text-white placeholder:text-white/35 focus-visible:ring-[#e5c15b]"
              />
              <Button
                onClick={onVerifyCode}
                disabled={loading || code.length !== 6}
                className="h-14 w-full rounded-xl bg-[#bf2327] text-base font-semibold text-white shadow-lg shadow-red-950/30 hover:bg-[#d12c31]"
                data-testid="verify-code-btn"
              >
                {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Verificando...</> : "Entrar en mi portal"}
              </Button>
              <div className="text-center">
                {countdown > 0 ? (
                  <p className="text-sm text-white/45">Puedes solicitar otro código en {countdown}s</p>
                ) : (
                  <Button variant="link" onClick={onBack} className="text-[#e5c15b] hover:text-white">
                    Cambiar número o reenviar código
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        <button
          type="button"
          onClick={() => navigate("/m/" + restaurantId)}
          className="mt-7 flex w-full items-center justify-center gap-2 text-sm text-white/45 transition hover:text-white"
        >
          <ArrowLeft className="h-4 w-4" />
          Volver al menú
        </button>
      </div>
    </main>
  );
}
